import { useState } from "react";
import { Clock, Columns, Kanban, Plus, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PropertyDetailPage } from "@/components/find/detail/PropertyDetailPage";
import { KanbanBoard } from "@/components/pipeline/KanbanBoard";
import { ManageColumnsDialog } from "@/components/pipeline/ManageColumnsDialog";
import { NewDealDrawer } from "@/components/pipeline/NewDealDrawer";
import { PipelineAnalytics } from "@/components/pipeline/PipelineAnalytics";
import { PipelineTimeline } from "@/components/pipeline/PipelineTimeline";
import { StageNotesDialog } from "@/components/pipeline/StageNotesDialog";
import { usePipeline } from "@/hooks/use-pipeline";
import { useRefreshListingStatus } from "@/hooks/use-refresh-listing-status";
import { useSavedProperties } from "@/hooks/use-saved-properties";
import type { MockProperty } from "@/data/mockProperties";

/**
 * Pipeline page (Phase 2).
 *
 * Header actions: Manage Columns (opens ManageColumnsDialog) · New Deal
 * (opens NewDealDrawer). Tab 1: drag-drop KanbanBoard. Tab 2: Timeline view.
 *
 * Card clicks still open PropertyDetailModal — that's the legacy detail
 * modal, slated for full rewrite in Phase 3.
 */
export default function Pipeline() {
  const {
    pipelines,
    savedProperties,
    isLoading,
    isLoadingStageHistory,
    getHistoryForProperty,
    getPropertiesByPipeline,
    updateStageNote,
    updatePropertyNotes,
    refetch,
    stageHistory,
  } = usePipeline();

  // The KPI row and the Timeline tab are computed from the stage-history audit
  // log, so they wait for it; the board only needs deals + stages.
  const isLoadingHistoryViews = isLoading || isLoadingStageHistory;

  // Re-checks the property feed for deals we don't own yet, so a listing that
  // sold to another buyer shows up on the board instead of silently going stale.
  const refreshListingStatus = useRefreshListingStatus();

  const [activeTab, setActiveTab] = useState("board");
  const [manageOpen, setManageOpen] = useState(false);
  const [newDealOpen, setNewDealOpen] = useState(false);
  const [detailProperty, setDetailProperty] = useState<MockProperty | null>(
    null,
  );
  const [notesTarget, setNotesTarget] = useState<{
    id: string;
    property: MockProperty;
    stage: string;
    notes: string | null;
  } | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);

  // useSavedProperties is loaded by usePipeline (composed), but we don't need
  // its handlers here — KanbanBoard owns the drag handlers, NewDealDrawer
  // owns the promote flow. We compose it implicitly through usePipeline.
  useSavedProperties();

  function openDealDetail(dealId: string) {
    const row = savedProperties.find((p) => p.id === dealId);
    if (!row) return;
    setDetailProperty(row.property_data as unknown as MockProperty);
  }

  // When a deal is opened, swap the kanban for the in-place detail page
  // (mirrors the find-page mockup's body.detail-open behavior).
  if (detailProperty) {
    return (
      <div className="flex h-full flex-col overflow-y-auto">
        <PropertyDetailPage
          property={detailProperty}
          fromPipeline
          onBack={() => setDetailProperty(null)}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col space-y-4 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Pipeline</h1>
          <p className="text-sm text-muted-foreground">
            Track every deal from watchlist to close.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => refreshListingStatus.mutate({})}
            disabled={isLoading || refreshListingStatus.isPending}
            title="Re-check the property feed for deals you don't own yet"
          >
            <RefreshCw
              className={cn(
                "h-4 w-4",
                refreshListingStatus.isPending && "animate-spin",
              )}
              aria-hidden
            />
            {refreshListingStatus.isPending ? "Checking…" : "Refresh Market Status"}
          </Button>
          <Button
            variant="outline"
            onClick={() => setManageOpen(true)}
            disabled={isLoading}
          >
            <Columns className="h-4 w-4" aria-hidden /> Manage Columns
          </Button>
          <Button onClick={() => setNewDealOpen(true)} disabled={isLoading}>
            <Plus className="h-4 w-4" aria-hidden /> New Deal
          </Button>
        </div>
      </div>

      {/* KPI row — skeleton while loading */}
      {isLoadingHistoryViews ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-card border border-border rounded-[10px] px-4 py-3.5 flex items-center gap-3">
              <Skeleton className="h-9 w-9 rounded-lg flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-2.5 w-32" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <PipelineAnalytics
          pipelines={pipelines}
          getPropertiesByPipeline={getPropertiesByPipeline as never}
          savedProperties={savedProperties as never}
          stageHistory={stageHistory}
        />
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex min-h-0 flex-1 flex-col"
      >
        <TabsList className="w-fit">
          <TabsTrigger value="board" className="gap-1.5">
            <Kanban className="h-3.5 w-3.5" aria-hidden /> Board
          </TabsTrigger>
          <TabsTrigger value="timeline" className="gap-1.5">
            <Clock className="h-3.5 w-3.5" aria-hidden /> Timeline
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="board"
          className="mt-3 flex min-h-0 flex-col overflow-hidden data-[state=active]:flex-1"
        >
          {isLoading ? (
            /* Skeleton matches the actual kanban column + card layout */
            <div className="flex gap-2.5 overflow-x-auto pb-4 h-full">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="w-[290px] flex-shrink-0 flex flex-col rounded-lg border border-border bg-card overflow-hidden">
                  {/* Column header */}
                  <div className="flex items-center justify-between px-3 py-2.5 gap-2">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-2 w-2 rounded-full" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                    <Skeleton className="h-5 w-7 rounded-full" />
                  </div>
                  {/* Cards */}
                  <div className="flex flex-col gap-2 p-2 flex-1">
                    {i === 0 || i === 6 ? (
                      /* First and last columns show a card skeleton */
                      <div className="rounded-lg border border-border bg-card overflow-hidden">
                        <Skeleton className="h-[140px] w-full rounded-none" />
                        <div className="p-3 space-y-2">
                          <Skeleton className="h-3.5 w-3/4" />
                          <Skeleton className="h-3 w-1/2" />
                          <Skeleton className="h-4 w-1/3" />
                          <div className="flex gap-3">
                            <Skeleton className="h-3 w-8" />
                            <Skeleton className="h-3 w-8" />
                            <Skeleton className="h-3 w-10" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center flex-1 gap-2 text-muted-foreground/30 py-8">
                        <Skeleton className="h-5 w-5" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : pipelines.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-muted/30 p-12 text-center">
              <Columns
                className="mx-auto mb-3 h-10 w-10 text-muted-foreground/60"
                aria-hidden
              />
              <p className="text-foreground">
                No pipeline stages yet for this workspace.
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Add your first stage in Manage Columns, or apply the seed
                migration to load defaults.
              </p>
              <Button
                onClick={() => setManageOpen(true)}
                className="mt-4"
                variant="outline"
              >
                Open Manage Columns
              </Button>
            </div>
          ) : (
            <KanbanBoard
              onOpenDeal={openDealDetail}
              onAddDeal={() => setNewDealOpen(true)}
            />
          )}
        </TabsContent>

        <TabsContent value="timeline" className="mt-3">
          {!isLoadingHistoryViews && savedProperties.length > 0 ? (
            <PipelineTimeline
              pipelines={pipelines}
              savedProperties={savedProperties as never}
              getHistoryForProperty={getHistoryForProperty}
            />
          ) : (
            <div className="py-8 text-center">
              <Clock
                className="mx-auto mb-3 h-12 w-12 text-muted-foreground/40"
                aria-hidden
              />
              <p className="text-muted-foreground">No timeline data yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Move properties between stages to track timeline
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Manage Columns */}
      <ManageColumnsDialog open={manageOpen} onOpenChange={setManageOpen} />

      {/* New Deal drawer */}
      <NewDealDrawer
        open={newDealOpen}
        onOpenChange={setNewDealOpen}
        onPromoted={() => {
          void refetch();
        }}
      />

      {/* Notes dialog */}
      {notesTarget && (
        <StageNotesDialog
          open={notesOpen}
          onOpenChange={setNotesOpen}
          pipelines={pipelines}
          propertyAddress={notesTarget.property.address}
          currentStage={notesTarget.stage}
          currentNotes={notesTarget.notes}
          stageHistory={getHistoryForProperty(notesTarget.id)}
          onSaveCurrentNote={async (notes) => {
            await updatePropertyNotes(notesTarget.id, notes);
            setNotesTarget((prev) => (prev ? { ...prev, notes } : null));
          }}
          onSaveStageNote={updateStageNote}
        />
      )}
    </div>
  );
}
